import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { DataProviderInstance } from '../../../../api/DataProvider';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../../components/buttons/CustomButton';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { USER_ROLES } from '../../../../constants/userRoles';
import ConfirmPopup from '../../../../popup/ConfirmPopup/ConfirmPopup';
import Toast from '../../../../components/support/Toast';
import ProviderListCard from './components/ProviderListCard';
import ProviderFormPopup from './components/ProviderFormPopup';
import InsuranceProceduresPopup from './components/InsuranceProceduresPopup';
import styles from './CompanyInsurance.module.css';

const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR', { sensitivity: 'base' });

const EMPTY_FORM = {
    name: '',
    ans_registry: '',
    notes: '',
    requires_medical_request: false,
    requires_authorization: false,
    // { id?, name, active } — plano sem id ainda não existe no servidor
    plans: [],
    active: true
};

const normalizeName = (value) => String(value || '').trim().toLowerCase();

/**
 * Convênios da empresa (/company/insurance): operadoras e planos atendidos.
 * Orquestra lista, formulário e ativar/inativar. Nunca exclui: as carteirinhas e
 * os agendamentos antigos continuam apontando para a operadora.
 * O DataProvider recarrega CompanyConfig.InsuranceProviders depois de salvar.
 */
const CompanyInsurance = () => {
    const { GetInsuranceProviders, SetInsuranceProvider, UpdateInsuranceProvider } = DataProviderInstance();
    const { hasRole } = useRoleAccess();

    // Só proprietário e administradores editam (mesma regra das salas)
    const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [localLoading, setLocalLoading] = useState(false);
    const [pageError, setPageError] = useState('');
    const [inactivateConfirm, setInactivateConfirm] = useState(null);
    // Operadora com a tabela de procedimentos aberta (lida da lista, que recarrega depois de salvar)
    const [proceduresProviderId, setProceduresProviderId] = useState(null);
    const proceduresProvider = providers.find((provider) => String(provider.id) === String(proceduresProviderId)) || null;
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const loadProviders = (silent = false) => {
        if (!silent) setLoading(true);
        GetInsuranceProviders((result) => {
            if (result?.status === 'SUCCESS' && Array.isArray(result.data)) {
                setProviders([...result.data].sort(byName));
            } else if (result?.error) {
                setPageError(result.error);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        loadProviders();
    }, []);

    const validateForm = () => {
        const newErrors = {};
        const name = formData.name.trim();

        if (!name) {
            newErrors.name = 'Nome da operadora é obrigatório';
        } else if (name.length < 2) {
            newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
        } else if (name.length > 100) {
            newErrors.name = 'Nome deve ter no máximo 100 caracteres';
        }

        // Nome repetido (exceto a própria operadora em edição)
        const existing = providers.find((provider) =>
            normalizeName(provider.name) === normalizeName(name) &&
            String(provider.id) !== String(editingProvider?.id)
        );
        if (existing) {
            newErrors.name = 'Já existe uma operadora com este nome';
        }

        if (formData.ans_registry && formData.ans_registry.trim().length > 20) {
            newErrors.ans_registry = 'Registro ANS deve ter no máximo 20 caracteres';
        }

        if (formData.notes && formData.notes.length > 500) {
            newErrors.notes = 'Observações devem ter no máximo 500 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setEditingProvider(null);
        setShowForm(false);
        setErrors({});
    };

    const handleSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!validateForm()) return;

        setLocalLoading(true);
        const base = {
            name: formData.name.trim(),
            ans_registry: formData.ans_registry.trim(),
            notes: formData.notes.trim(),
            requires_medical_request: formData.requires_medical_request ? 1 : 0,
            requires_authorization: formData.requires_authorization ? 1 : 0
        };

        if (editingProvider) {
            // Planos removidos vão com active 0; os novos sem id são criados
            const plans = formData.plans.map((plan) => ({
                ...(plan.id ? { id: plan.id } : {}),
                name: plan.name,
                active: plan.active ? 1 : 0
            }));
            UpdateInsuranceProvider({ ...base, id: editingProvider.id, active: formData.active ? 1 : 0, plans }, (result) => {
                if (result && !result.error) {
                    loadProviders(true);
                    resetForm();
                    showToast('Operadora atualizada');
                } else {
                    setErrors({ general: result?.error || 'Erro ao atualizar operadora' });
                }
                setLocalLoading(false);
            });
        } else {
            const plans = formData.plans.filter((plan) => plan.active).map((plan) => plan.name);
            SetInsuranceProvider({ ...base, plans }, (result) => {
                if (result && !result.error) {
                    loadProviders(true);
                    resetForm();
                    showToast('Operadora cadastrada');
                } else {
                    setErrors({ general: result?.error || 'Erro ao cadastrar operadora' });
                }
                setLocalLoading(false);
            });
        }
    };

    const startEdit = (provider) => {
        setFormData({
            name: provider.name || '',
            ans_registry: provider.ans_registry || '',
            notes: provider.notes || '',
            requires_medical_request: Number(provider.requires_medical_request) === 1,
            requires_authorization: Number(provider.requires_authorization) === 1,
            plans: (provider.plans || []).map((plan) => ({ id: plan.id, name: plan.name, active: Number(plan.active) === 1 })),
            active: Number(provider.active) === 1
        });
        setEditingProvider(provider);
        setShowForm(true);
        setErrors({});
    };

    // Inativar pede confirmação (como em Salas); ativar segue direto
    const handleToggleClick = (provider) => {
        if (Number(provider.active) === 1) {
            setInactivateConfirm(provider);
        } else {
            toggleProviderStatus(provider);
        }
    };

    const toggleProviderStatus = (provider) => {
        setLocalLoading(true);
        setPageError('');
        const nextActive = Number(provider.active) === 1 ? 0 : 1;
        const plans = (provider.plans || []).map((plan) => ({ id: plan.id, name: plan.name, active: Number(plan.active) }));

        UpdateInsuranceProvider({
            id: provider.id,
            name: provider.name,
            ans_registry: provider.ans_registry || '',
            notes: provider.notes || '',
            active: nextActive,
            plans
        }, (result) => {
            if (result && !result.error) {
                setProviders((prev) => prev.map((item) => (
                    String(item.id) === String(provider.id) ? { ...item, active: nextActive } : item
                )));
                showToast(nextActive ? 'Operadora ativada' : 'Operadora inativada');
            } else {
                setPageError(result?.error || 'Não foi possível mudar a situação da operadora. Tente de novo.');
            }
            setLocalLoading(false);
        });
    };

    const handleAdd = () => {
        setFormData(EMPTY_FORM);
        setEditingProvider(null);
        setErrors({});
        setShowForm(true);
    };

    return (
        <div className={`page-content ${styles.mainContainer}`}>
            <div className={styles.headerContainer}>
                <AreaTitle
                    Title="Convênios"
                    Icon={<BadgeOutlinedIcon />}
                    Description="As operadoras e os planos que você atende, para marcar atendimentos por convênio."
                />
                <Tooltip
                    title={!canEdit ? 'Apenas proprietários e administradores podem gerenciar convênios' : ''}
                    arrow
                >
                    <span>
                        <CustomButton
                            style="main"
                            variant="contained"
                            icon={<AddRoundedIcon />}
                            label="Nova operadora"
                            onClick={handleAdd}
                            enabled={canEdit}
                        />
                    </span>
                </Tooltip>
            </div>

            {pageError && (
                <div className={styles.errorMessage} role="alert">{pageError}</div>
            )}

            <ProviderListCard
                loading={loading && providers.length === 0}
                providers={providers}
                canEdit={canEdit}
                busy={localLoading}
                onAdd={handleAdd}
                onEdit={startEdit}
                onToggle={handleToggleClick}
                onProcedures={(provider) => setProceduresProviderId(provider.id)}
            />

            {proceduresProvider && (
                <InsuranceProceduresPopup
                    provider={proceduresProvider}
                    providers={providers}
                    canEdit={canEdit}
                    onChanged={(message) => { loadProviders(true); showToast(message); }}
                    onClose={() => setProceduresProviderId(null)}
                />
            )}

            {showForm && (
                <ProviderFormPopup
                    isEdit={!!editingProvider}
                    formData={formData}
                    errors={errors}
                    saving={localLoading}
                    onFieldChange={(name, value) => {
                        setFormData((prev) => ({ ...prev, [name]: value }));
                        setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
                    }}
                    onSubmit={handleSubmit}
                    onClose={resetForm}
                />
            )}

            <ConfirmPopup
                Visibility={!!inactivateConfirm}
                Title="Inativar operadora"
                Description={`A operadora "${inactivateConfirm?.name || ''}" sai das opções na hora de marcar e de registrar carteirinhas, mas o histórico continua. Dá para ativar depois.`}
                ConfirmLabel="Inativar"
                OnConfirm={() => toggleProviderStatus(inactivateConfirm)}
                OnHide={() => setInactivateConfirm(null)}
            />

            <Toast
                open={toast.open}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                message={toast.message}
                severity={toast.severity}
                autoHideDuration={6000}
            />
        </div>
    );
};

export default CompanyInsurance;
